import { FC, RefObject, useCallback, useRef, useState } from 'react';
import Slider, { ResponsiveObject } from 'react-slick';
import { Box, IconButton } from '@mui/material';
import cn from 'classnames';

import 'slick-carousel/slick/slick.css';
import './styles.scss';

interface IProps {
  children?: React.ReactNode;
  responsive?: ResponsiveObject[];
  slidesToShow?: number;
  initialSlide?: number;
  showArrows?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  onInit?: () => void;
}

const CustomButton = ({ className, style, onClick, children }: any) => (
  <IconButton
    onClick={onClick}
    color="success"
    style={style}
    className={cn('image-slick-carousel__arrow', className)}
  >
    {children}
  </IconButton>
);

const CustomLeftButton = (props: any) => {
  const { className, ...rest } = props;
  const buttonProps = {
    ...rest,
    className: 'image-slick-carousel__arrow--left',
  };
  return (
    <CustomButton {...buttonProps}>
      <Box width={24} height={24} component="img" src="/images/img-arrow_back_ios.svg" />
    </CustomButton>
  );
};

const CustomRightButton = (props: any) => {
  const { className, ...rest } = props;
  const buttonProps = {
    ...rest,
    className: 'image-slick-carousel__arrow--right',
  };
  return (
    <CustomButton {...buttonProps}>
      <Box width={24} height={24} component="img" src="/images/img-arrow_forward_ios.svg" />
    </CustomButton>
  );
};

const settings = {
  dots: false,
  infinite: false,
  speed: 250,
  slidesToScroll: 1,
  slidesToShow: 4,
  arrows: true,
  useCSS: false,
  nextArrow: <CustomRightButton />,
  prevArrow: <CustomLeftButton />,
  className: 'image-slick-carousel',
};

const ImageSlickCarousel: FC<IProps> = ({
  children,
  responsive,
  slidesToShow,
  initialSlide,
  showArrows,
  containerRef,
  onInit,
}): JSX.Element => {
  const [showCarouselArrows, setShowCarouselArrows] = useState<boolean>(false);

  const onSliderInit = useCallback(() => {
    if (onInit) {
      onInit();
    }
    if (typeof showArrows === 'boolean') {
      return setShowCarouselArrows(showArrows);
    }
    if (containerRef?.current) {
      const sliderTrack = containerRef?.current?.querySelector('.slick-track');
      const sliderTrackWidth = sliderTrack?.clientWidth || 0;
      const containerPosition = containerRef?.current?.getBoundingClientRect();
      const trackPosition = sliderTrack?.getBoundingClientRect();
      if (sliderTrack && sliderTrackWidth < (containerRef?.current?.clientWidth || 0)) {
        (sliderTrack as HTMLElement).style.transform = 'translate3d(0px, 0px, 0px)';
      }
      if (
        containerPosition &&
        trackPosition &&
        trackPosition.left - containerPosition.left - 40 < 0
      ) {
        setShowCarouselArrows(true);
        return;
      }
      setShowCarouselArrows(sliderTrackWidth > (containerRef?.current?.clientWidth || 0));
      return;
    }
    return setShowCarouselArrows(true);
  }, [containerRef?.current, showArrows]);

  const sliderProps = {
    ...settings,
    ...(!!responsive && { responsive }),
    ...(!!slidesToShow && { slidesToShow }),
    ...(typeof initialSlide === 'number' && { initialSlide }),
    ...{ arrows: showCarouselArrows },
    ...{ onInit: onSliderInit },
    ...{ onReInit: onSliderInit },
  };
  return <Slider {...sliderProps}>{children}</Slider>;
};

export default ImageSlickCarousel;
